import { useEffect, useState, useCallback } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import api from '../api/client';
import TasksByTagProgressPanel from '../components/TasksByTagProgressPanel';
import { useAuth } from '../context/AuthContext';
import { useTabIdentity } from '../hooks/useTabIdentity';

/**
 * Прогресс по темам: заметки преподавателей (вид 1) и заглушка журнала (вид 2).
 * Ученик: /topic-progress.
 * Преподаватель: /teacher/students/:studentId/topic-progress
 * Родитель: /parent/children/:studentId/topic-progress (только чтение)
 */
export default function StudentTopicProgressPage() {
  const { studentId: routeStudentId } = useParams();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const [mainTab, setMainTab] = useState('notes'); // 'notes' | 'journal'
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveOk, setSaveOk] = useState(false);

  const isTeacherCabinetRoute = Boolean(routeStudentId) && location.pathname.startsWith('/teacher/');
  const isParentRoute = Boolean(routeStudentId) && location.pathname.startsWith('/parent/');
  const targetStudentId = (isTeacherCabinetRoute || isParentRoute)
    ? Number(routeStudentId)
    : user?.id;

  useTabIdentity(
    (isTeacherCabinetRoute || isParentRoute) && routeStudentId
      ? {
          title: 'Темы',
          badge: 'Т',
          colorKey: `student:${routeStudentId}`,
        }
      : null,
  );

  const loadNotes = useCallback(async (opts = {}) => {
    const silent = opts.silent === true;
    if (!targetStudentId || Number.isNaN(targetStudentId)) return;
    if (!silent) setLoading(true);
    setError('');
    try {
      const res = await api.get(`/teacher/students/${targetStudentId}/topic-progress-notes/`);
      const list = Array.isArray(res.data) ? res.data : [];
      setItems(list);
      const mine = list.find((row) => row.is_me);
      if (mine) setEditNotes(mine.notes ?? '');
    } catch (e) {
      const msg = e.response?.data?.detail || e.response?.data?.message || 'Не удалось загрузить заметки.';
      setError(typeof msg === 'string' ? msg : 'Не удалось загрузить заметки.');
      setItems([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [targetStudentId]);

  useEffect(() => {
    if (!authLoading && user && targetStudentId) {
      loadNotes();
    }
  }, [authLoading, user, targetStudentId, loadNotes]);

  useEffect(() => {
    if (!saveOk) return;
    const t = setTimeout(() => setSaveOk(false), 2500);
    return () => clearTimeout(t);
  }, [saveOk]);

  if (!authLoading && !user) {
    return <Navigate to="/login" replace />;
  }

  if (!isTeacherCabinetRoute && !isParentRoute && user && !user.is_student) {
    return <Navigate to="/" replace />;
  }

  async function handleSaveNotes() {
    if (!isTeacherCabinetRoute || !targetStudentId) return;
    setSaving(true);
    setSaveError('');
    try {
      await api.patch(`/teacher/students/${targetStudentId}/topic-progress-notes/`, {
        notes: editNotes,
      });
      setSaveOk(true);
      await loadNotes({ silent: true });
    } catch (e) {
      const msg = e.response?.data?.detail || 'Не удалось сохранить.';
      setSaveError(typeof msg === 'string' ? msg : 'Не удалось сохранить.');
    } finally {
      setSaving(false);
    }
  }

  if (authLoading || (user && targetStudentId && loading && !error)) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
        <p>Загрузка...</p>
      </div>
    );
  }

  const pageTitle = isTeacherCabinetRoute
    ? 'Прогресс по темам ученика'
    : isParentRoute
      ? 'План занятий'
      : 'Мой прогресс по темам';

  return (
    <div className="student-topic-progress-page">
      <div className="page-header">
        <h1>{pageTitle}</h1>
        <p className="page-subtitle">
          {isTeacherCabinetRoute
            ? 'Заметки всех преподавателей с активной связью с этим учеником. Вы редактируете только свою заметку.'
            : isParentRoute
              ? 'Актуальный план работы и заметки преподавателей. Редактировать может только автор заметки.'
              : 'Заметки преподавателей о вашем прогрессе по темам. Редактировать может только автор заметки.'}
        </p>
        {isTeacherCabinetRoute ? (
          <p className="page-subtitle">
            <Link to={`/teacher/students/${routeStudentId}`} className="back-link">
              ← Работа с учеником
            </Link>
          </p>
        ) : isParentRoute ? (
          <p className="page-subtitle">
            <Link to="/parent" className="back-link">
              ← Кабинет родителя
            </Link>
            {' · '}
            <Link to={`/parent/children/${routeStudentId}/activity`}>Журнал попыток</Link>
          </p>
        ) : (
          <p className="page-subtitle">
            <Link to="/my-teachers" className="back-link">
              ← Мои преподаватели
            </Link>
          </p>
        )}
      </div>

      {!isParentRoute && (
        <div className="topic-progress-main-tabs" role="tablist" aria-label="Вид страницы">
          <button
            type="button"
            role="tab"
            aria-selected={mainTab === 'notes'}
            className={`btn btn-sm ${mainTab === 'notes' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setMainTab('notes')}
          >
            Заметки по темам
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mainTab === 'journal'}
            className={`btn btn-sm ${mainTab === 'journal' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setMainTab('journal')}
          >
            Журнал задач по темам
          </button>
        </div>
      )}

      {mainTab === 'journal' && !isParentRoute && (
        <div className="topic-progress-stub-card" role="tabpanel">
          <TasksByTagProgressPanel studentId={targetStudentId} />
        </div>
      )}

      {mainTab === 'notes' && (
        <div className="topic-progress-notes-panel" role="tabpanel">
          {error && <div className="alert alert-error">{error}</div>}
          {saveError && <div className="alert alert-error">{saveError}</div>}
          {saveOk && <div className="alert alert-success">Сохранено.</div>}

          {!error && items.length === 0 && !loading && (
            <p className="text-secondary">
              Нет активных связей с преподавателями — пока нечего показать.
            </p>
          )}

          {items.map((item) => {
            const updatedLabel = item.updated_at
              ? new Date(item.updated_at).toLocaleString('ru-RU')
              : 'ещё не обновлялось';

            const inner = (
              <div className="progress-note-card-inner">
                <div className="progress-note-meta text-secondary">
                  Обновлено: {updatedLabel}
                </div>
                {isTeacherCabinetRoute && item.is_me ? (
                  <div className="form-group">
                    <label htmlFor={`notes-edit-${item.teacher_id}`}>Текст заметки</label>
                    <textarea
                      id={`notes-edit-${item.teacher_id}`}
                      className="form-control"
                      rows={8}
                      value={editNotes}
                      onChange={(e) => setEditNotes(e.target.value)}
                      disabled={saving}
                    />
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={saving}
                      onClick={handleSaveNotes}
                    >
                      {saving ? 'Сохранение…' : 'Сохранить'}
                    </button>
                  </div>
                ) : (
                  <pre className="progress-note-readonly">{item.notes || '—'}</pre>
                )}
              </div>
            );

            if (isTeacherCabinetRoute && !item.is_me) {
              return (
                <details key={item.teacher_id} className="progress-note-card">
                  <summary className="progress-note-summary">{item.teacher_display_name}</summary>
                  {inner}
                </details>
              );
            }

            return (
              <section key={item.teacher_id} className="progress-note-card progress-note-card-expanded">
                <h2 className="progress-note-title">{item.teacher_display_name}</h2>
                {inner}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
